import * as tslib_1 from "tslib";
/** @jsx jsx */

import { jsx } from "@emotion/core";
import * as React from "react";
import ReachAlert from "@reach/alert";
import { Text } from "./Text";
import { CloseButton } from "./IconButton";
import PropTypes from "prop-types";
import { useTheme } from "./Theme/Providers";
import { IconWrapper } from "./IconWrapper";
import { IconInfo, IconCheckCircle, IconAlertCircle, IconAlertOctagon, IconHelpCircle } from "./Icons";

var alertIntentions = function alertIntentions(theme) {
  return {
    info: theme.colors.intent.none,
    success: theme.colors.intent.success,
    question: theme.colors.intent.primary,
    danger: theme.colors.intent.danger,
    warning: theme.colors.intent.warning
  };
};

var icons = {
  info: jsx(IconInfo, null),
  success: jsx(IconCheckCircle, null),
  warning: jsx(IconAlertCircle, null),
  danger: jsx(IconAlertOctagon, null),
  question: jsx(IconHelpCircle, null)
};
/**
 * Use an alert to inform users of important information.
 * To display an alert in a toast notification, use the toast module.
 */

export var Alert = function Alert(_a) {
  var children = _a.children,
      title = _a.title,
      id = _a.id,
      subtitle = _a.subtitle,
      component = _a.component,
      _b = _a.elevation,
      elevation = _b === void 0 ? "xs" : _b,
      onRequestClose = _a.onRequestClose,
      _c = _a.intent,
      intent = _c === void 0 ? "info" : _c,
      other = tslib_1.__rest(_a, ["children", "title", "id", "subtitle", "component", "elevation", "onRequestClose", "intent"]);

  var theme = useTheme();
  var intentions = React.useMemo(function () {
    return alertIntentions(theme);
  }, [theme]);
  var dark = theme.colors.mode === "dark";
  var color = intentions[intent];
  var accent = dark ? color.light : color.base;

  if (dark && intent === "info") {
    accent = color.light;
  }

  var icon = icons[intent];
  var contents = title ? jsx("div", {
    className: "Alert__title",
    css: {
      display: "flex",
      alignItems: "flex-start"
    }
  }, jsx("div", {
    css: {
      flex: "0 0 auto",
      marginTop: "2px"
    }
  }, jsx(IconWrapper, {
    size: "md",
    color: accent
  }, icon)), jsx("div", {
    css: {
      marginLeft: theme.spaces.md
    }
  }, jsx(Text, {
    className: "Alert__title-text",
    id: id,
    css: {
      margin: 0
    },
    variant: "h6"
  }, title), subtitle && jsx(Text, {
    className: "Alert__title-subtitle",
    muted: true,
    css: {
      fontSize: theme.fontSizes[0]
    }
  }, subtitle))) : children;
  var Component = component || ReachAlert;
  return jsx(Component, tslib_1.__assign({
    className: "Alert",
    css: {
      backgroundColor: dark ? theme.colors.background.tint1 : theme.colors.background.default,
      overflow: "hidden",
      position: "relative",
      boxShadow: theme.shadows[elevation],
      borderRadius: theme.radii.md
    }
  }, other), jsx("div", null, jsx("div", {
    className: "Alert__bar",
    css: {
      width: theme.radii.md,
      position: "absolute",
      top: 0,
      left: 0,
      bottom: 0,
      backgroundColor: accent
    }
  }), jsx("div", {
    className: "Alert__content",
    css: {
      display: "flex",
      alignItems: "flex-start",
      padding: theme.spaces.md + " " + theme.spaces.md,
      paddingRight: onRequestClose ? "3.5rem" : undefined
    }
  }, contents, onRequestClose && jsx(CloseButton, {
    css: {
      marginTop: "-0.45rem",
      right: theme.spaces.sm,
      position: "absolute"
    },
    onClick: onRequestClose
  }))));
};
Alert.displayName = "Alert";
Alert.propTypes = {
  onRequestClose: PropTypes.func,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node,
  intent: PropTypes.oneOf(["info", "success", "warning", "danger", "question"])
};